import { type Clip } from '@air/api/types';
import { type AppNode } from '@air/feature-edit-canvas';

import { clipToAppNode, DEFAULT_DIMENSION, NODE_SPACING } from '~/components/Modals/AddToCanvasModal/clipToAppNode';

/** Columns used when a whole board is placed on a canvas. A single row runs off the canvas. */
export const CANVAS_CLIP_GRID_COLUMNS = 5;

export type AddToCanvasLayout = 'row' | 'wrap';

export type ClipsRowLayout = {
  /** Left edge of the first node. */
  startX?: number;
  /** Top edge of the first row. */
  startY?: number;
  /** Gap between nodes, horizontally and between wrapped rows. */
  spacing?: number;
  /** When set, wrap onto a new row after this many nodes. */
  columns?: number;
};

export type ClipCanvasPlacement = {
  height: number;
  width: number;
  x: number;
  y: number;
};

/**
 * Positions clips in a row, wrapping after `columns` nodes. Each new row starts
 * below the tallest node in the row above so mixed aspect ratios do not overlap.
 */
export const getClipCanvasPlacements = (
  clips: Pick<Clip, 'width' | 'height'>[],
  { startX = 0, startY = 0, spacing = NODE_SPACING, columns }: ClipsRowLayout = {},
): ClipCanvasPlacement[] => {
  let offsetX = 0;
  let rowY = startY;
  let column = 0;
  let rowHeight = 0;

  return clips.map((clip) => {
    const width = clip.width || DEFAULT_DIMENSION;
    const height = clip.height || DEFAULT_DIMENSION;

    if (columns && column >= columns) {
      rowY += rowHeight + spacing;
      offsetX = 0;
      column = 0;
      rowHeight = 0;
    }

    const placement = { height, width, x: startX + offsetX, y: rowY };

    offsetX += width + spacing;
    rowHeight = Math.max(rowHeight, height);
    column += 1;

    return placement;
  });
};

/**
 * Converts clips to canvas nodes.
 *
 * Pass `startX`/`startY` to append beside existing canvas content so new nodes
 * don't land on top of what's already there. With no layout it starts at the
 * origin, which is what the empty/new-canvas flows want. Pass `columns` to wrap
 * a large set (a whole board) instead of one horizontal row.
 */
export const clipsToAppNodes = (clips: Clip[], layout: ClipsRowLayout = {}): AppNode[] => {
  const placements = getClipCanvasPlacements(clips, layout);

  return placements.flatMap((placement, index) => {
    const clip = clips[index];

    if (!clip) {
      return [];
    }

    return [clipToAppNode(clip, { x: placement.x, y: placement.y })];
  });
};
